import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { SearchBar } from './SearchBar';
import { LoadingSpinner, ContextCardSkeleton } from './LoadingSpinner';

const GET_CONTEXTS = gql`
  query GetContexts {
    contexts {
      id
      name
      version
      type
      content
      tags
      created_at
    }
  }
`;

export function ContextList() {
  const [searchQuery, setSearchQuery] = useState('');
  const { data, loading, error } = useQuery(GET_CONTEXTS);

  if (loading) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">Contexts</h1>
        <div className="grid gap-4">
          <ContextCardSkeleton />
          <ContextCardSkeleton />
          <ContextCardSkeleton />
        </div>
      </div>
    );
  }

  if (error) return <div className="p-4 text-red-500">Error: {error.message}</div>;

  const contexts = data?.contexts || [];

  // Filter contexts based on search query
  const filteredContexts = contexts.filter((context: any) => {
    const query = searchQuery.toLowerCase();
    return (
      context.name.toLowerCase().includes(query) ||
      context.type.toLowerCase().includes(query) ||
      context.content.toLowerCase().includes(query) ||
      context.tags.some((tag: string) => tag.toLowerCase().includes(query))
    );
  });

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">
        Contexts
        <span className="ml-2 text-sm font-normal text-gray-500">
          ({filteredContexts.length} {filteredContexts.length === 1 ? 'context' : 'contexts'})
        </span>
      </h1>

      <div className="mb-4">
        <SearchBar onSearch={setSearchQuery} />
      </div>

      {filteredContexts.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {searchQuery ? (
            <>
              <div className="text-4xl mb-2">🔍</div>
              <div className="text-lg">No contexts found</div>
              <p className="text-sm mt-2">Try a different search term</p>
            </>
          ) : (
            <>
              <div className="text-4xl mb-2">📝</div>
              <div className="text-lg">No contexts yet</div>
              <p className="text-sm mt-2">Create your first context to get started</p>
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredContexts.map((context: any) => (
            <Link
              key={context.id}
              to={`/context/${context.id}`}
              className="border rounded-lg p-4 hover:border-primary hover:shadow-md transition block"
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold">{context.name}</h3>
                <span className="text-sm text-gray-500">v{context.version}</span>
              </div>
              <p className="text-sm text-gray-600 mb-2 capitalize">{context.type}</p>
              <p className="text-sm mb-2 line-clamp-2">{context.content}</p>
              {context.tags.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {context.tags.map((tag: string) => (
                    <span key={tag} className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
